import { useUserStore } from '../../../store/useUserStore';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { GENDER, BOTTOM_NAVI } from '../../../data/define';
import useUpdateUser from '../../../hook/useUpdateUser';
import FullHeightContainer from '../../../component/common/FullHeightContainer';
import { useEditKidMutation, useEditKidProfileImageMutation } from '../../../queries';
import TopHeaderWithPrev from '../../../component/common/TopHeaderWithPrev';
import BottomNavigation from '../../../component/common/BottomNavigation';
import KidProfileEdit from '../../../component/my/KidProfileEdit';
import LoadingBackdrop from '../../../component/common/LoadingBackdrop';
import { Kid, Gender } from '../../../types';
import { useTranslation } from '../../../hook/useTranslation';

interface ProfileFile {
  file: File | null;
  url: string | null;
  name: string | null;
}

export default function KidProfileEditPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUserStore();
  const setUser = user.setUser;
  const { t } = useTranslation();
  const { updateUser } = useUpdateUser();
  const { kid } = (location.state as { kid: Kid & { imageUrl?: string } }) || {};
  const [loading, setLoading] = useState(false);
  const [profileFile, setProfileFile] = useState<ProfileFile | null>(null);
  const [name, setName] = useState(kid?.kidName || '');
  const [birthInput, setBirthInput] = useState<string | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [gender, setGender] = useState<Gender | string>(GENDER.UNKNOWN);

  const { mutateAsync: editKid } = useEditKidMutation(kid?.id || 0);
  const { mutateAsync: editKidProfileImage } = useEditKidProfileImageMutation(kid?.id || 0);

  // init kid data
  useEffect(() => {
    if (kid) {
      setProfileFile({
        file: null,
        url: kid?.imageUrl || null,
        name: null,
      });
      setName(kid?.kidName || '');
      setBirthInput(kid?.kidBirth || '');
      setGender(kid?.gender || GENDER.UNKNOWN);
    }
  }, [kid]);

  const handleSubmit = useCallback(async () => {
    if (!kid) return;
    if (!name) {
      toast.error(t('my.profile.toast_kid_name_required'));
      return;
    }
    try {
      setLoading(true);
      let profileImageUrl = null;
      if (profileFile && profileFile.file) {
        const fd = new FormData();
        fd.append('file', profileFile.file);
        const uploadRes: any = await editKidProfileImage(fd);
        profileImageUrl = uploadRes.data?.imageUrl;
      }
      await editKid({
        kidName: name,
        gender: gender as Gender,
        kidBirth: birthInput || '',
      });
      updateUser();
      navigate(-1);
      if (kid.id === user.mainKid?.id) {
        setUser((prev) => {
          return {
            ...prev,
            mainKid: prev.mainKid
              ? {
                  ...prev.mainKid,
                  imageUrl: profileImageUrl || prev.mainKid.imageUrl,
                }
              : null,
          };
        });
      }
    } catch (error) {
      toast.error(t('my.profile.toast_kid_edit_fail'));
      console.error('Error updating kid profile:', error);
    } finally {
      setLoading(false);
    }
  }, [
    kid,
    name,
    gender,
    user,
    birthInput,
    setUser,
    navigate,
    profileFile,
    updateUser,
    editKid,
    editKidProfileImage,
    t,
  ]);

  return (
    <FullHeightContainer>
      <TopHeaderWithPrev title={t('my.profile.kid_edit_title')} onClickPrev={() => navigate(-1)} />
      <button
        type="button"
        className="absolute top-3 right-4 font-[400] text-[16px] text-[#1A1A1A]"
        onClick={handleSubmit}
      >
        {t('common.done')}
      </button>
      <div className="pageInner">
        {loading && <LoadingBackdrop open={loading} />}
        <KidProfileEdit
          kid={kid}
          profileFile={profileFile}
          setProfileFile={setProfileFile}
          name={name}
          setName={setName}
          birthInput={birthInput}
          setBirthInput={setBirthInput}
          datePickerOpen={datePickerOpen}
          setDatePickerOpen={setDatePickerOpen}
          gender={gender}
          setGender={setGender}
        />
      </div>
      <BottomNavigation focusTab={BOTTOM_NAVI.MY} />
    </FullHeightContainer>
  );
}
